import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import StatusBadge from '../components/StatusBadge';
import PriorityMatrix from '../components/PriorityMatrix';
import FileAttachments from '../components/FileAttachments';
import LinkedProcessMaps from '../components/LinkedProcessMaps';
import ProcessSelector from '../components/ProcessSelector';
import ValueTrackingForm from '../components/ValueTrackingForm';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ArrowLeft, 
  Edit, 
  Trash2, 
  Send, 
  CheckCircle, 
  XCircle, 
  RotateCcw,
  MessageSquare,
  Paperclip,
  Loader2,
  FolderPlus,
  Clock,
  User,
  Building,
  MapPin,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const IdeaDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, isApprover } = useAuth();
  const [idea, setIdea] = useState(null);
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Dialog states
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [approvalAction, setApprovalAction] = useState('');
  const [approvalComment, setApprovalComment] = useState('');
  const [reviewFinancials, setReviewFinancials] = useState(null);
  const [reviewMetrics, setReviewMetrics] = useState([]);
  const [reviewSaving, setReviewSaving] = useState(false);
  const [budgetApproved, setBudgetApproved] = useState(null);
  const [budgetAmount, setBudgetAmount] = useState('');
  const [showBudgetSubmitted, setShowBudgetSubmitted] = useState(false);
  
  // Convert to project dialog
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectOwner, setProjectOwner] = useState('');
  const [startDate, setStartDate] = useState('');
  const [targetEndDate, setTargetEndDate] = useState('');

  useEffect(() => {
    fetchIdea();
    fetchComments();
    fetchAttachments();
    fetchUsers();
  }, [id]);

  const fetchIdea = async () => {
    try {
      const response = await api.get(`/ideas/${id}`);
      setIdea(response.data);
      setProjectName(response.data.title);
      // Init review financials from existing data
      setReviewFinancials(response.data.financial_tracking || {
        costs: { one_off_expected: 0, ongoing_expected: 0 },
        savings: { ongoing_expected: 0 }
      });
      setReviewMetrics(response.data.operational_metrics || []);
      setBudgetApproved(response.data.budget_approved ?? null);
      setBudgetAmount(response.data.budget_amount ? String(response.data.budget_amount) : '');
    } catch (error) {
      toast.error('Failed to load idea');
      navigate('/ideas');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await api.get(`/comments/idea/${id}`);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments');
    }
  };

  const fetchAttachments = async () => {
    try {
      const response = await api.get(`/attachments/list/idea/${id}`);
      setAttachments(response.data);
    } catch (error) {
      console.error('Failed to fetch attachments');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const handleSubmitIdea = async () => {
    setSubmitting(true);
    try {
      await api.post(`/ideas/${id}/submit`);
      toast.success('Idea submitted for review');
      fetchIdea();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit idea');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartReview = async () => {
    setSubmitting(true);
    try {
      await api.post(`/ideas/${id}/review`);
      toast.success('Review started');
      fetchIdea();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start review');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprovalAction = async () => {
    if (approvalAction === 'reject' && !approvalComment) {
      toast.error('Comment is required for rejection');
      return;
    }
    
    setSubmitting(true);
    try {
      // Save any review financials before approving
      if (reviewFinancials) {
        await api.put(`/ideas/${id}`, {
          financial_tracking: reviewFinancials,
          operational_metrics: reviewMetrics,
        });
      }
      await api.post(`/ideas/${id}/approve`, {
        action: approvalAction,
        comment: approvalComment || null
      });
      toast.success(`Idea ${approvalAction}d successfully`);
      setShowApprovalDialog(false);
      setApprovalComment('');
      fetchIdea();
      fetchComments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveReviewData = async () => {
    setReviewSaving(true);
    try {
      await api.put(`/ideas/${id}`, {
        financial_tracking: reviewFinancials,
        operational_metrics: reviewMetrics,
        budget_approved: budgetApproved,
        budget_amount: budgetApproved && budgetAmount ? parseFloat(budgetAmount) : null,
      });
      toast.success('Review data saved');
      fetchIdea();
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setReviewSaving(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    setSubmitting(true);
    try {
      await api.post('/comments', {
        content: newComment,
        entity_type: 'idea',
        entity_id: id
      });
      toast.success('Comment added');
      setNewComment('');
      fetchComments();
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await api.post(`/attachments/idea/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('File uploaded');
      fetchAttachments();
    } catch (error) {
      toast.error('Upload failed');
    }
  };

  const handleConvertToProject = async () => {
    if (!projectOwner || !startDate || !targetEndDate) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await api.post('/projects', {
        project_name: projectName,
        idea_id: id,
        owner_id: projectOwner,
        team_members: [],
        start_date: startDate,
        target_end_date: targetEndDate
      });
      toast.success('Project created successfully');
      navigate(`/projects/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this idea?')) return;
    
    try {
      await api.delete(`/ideas/${id}`);
      toast.success('Idea deleted');
      navigate('/ideas');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete idea');
    }
  };

  const canEdit = () => {
    if (isAdmin()) return true;
    if (isApprover()) return true;
    // Submitters can edit their own Draft or Rejected ideas
    if (idea?.submitter_id === user?.id && ['Draft', 'Rejected'].includes(idea?.status)) return true;
    return false;
  };

  const canDelete = () => {
    if (isAdmin()) return true;
    if (idea?.submitter_id === user?.id && idea?.status === 'Draft') return true;
    return false;
  };

  const canSubmit = () => {
    return ['Draft', 'Rejected'].includes(idea?.status) && (idea?.submitter_id === user?.id || isAdmin());
  };

  const canApprove = () => {
    return isApprover() && ['Submitted', 'Under Review'].includes(idea?.status);
  };

  const canConvert = () => {
    return isApprover() && idea?.status === 'Approved';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#235894]" />
      </div>
    );
  }

  if (!idea) return null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="idea-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
                {idea.title}
              </h1>
              <StatusBadge status={idea.status} />
              <StatusBadge status={idea.idea_type} />
            </div>
            <p className="text-slate-500 mt-1">
              Submitted by {idea.submitter_name} • {new Date(idea.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          {canEdit() && (
            <Button 
              variant="outline"
              onClick={() => navigate(`/ideas/${id}/edit`)}
              data-testid="edit-idea-btn"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
          {canDelete() && (
            <Button 
              variant="outline"
              onClick={handleDelete}
              className="text-red-600 hover:text-red-700"
              data-testid="delete-idea-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          )}
          {canSubmit() && (
            <Button 
              onClick={handleSubmitIdea}
              className="bg-[#235894] hover:bg-[#1E4B80]"
              disabled={submitting}
              data-testid="submit-idea-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Submit for Review
            </Button>
          )}
          {canConvert() && (
            <Button 
              onClick={() => setShowConvertDialog(true)}
              className="bg-[#26B8A7] hover:bg-[#1fa393]"
              data-testid="convert-to-project-btn"
            >
              <FolderPlus className="w-4 h-4 mr-2" />
              Convert to Project
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Problem Statement */}
          <Card>
            <CardHeader>
              <CardTitle>Problem Statement</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 whitespace-pre-wrap">{idea.problem_statement}</p>
              {idea.description && (
                <>
                  <Separator className="my-4" />
                  <h4 className="font-semibold text-slate-900 mb-2">Additional Details</h4>
                  <p className="text-slate-700 whitespace-pre-wrap">{idea.description}</p>
                </>
              )}
            </CardContent>
          </Card>

          {/* Details Grid */}
          <Card>
            <CardHeader>
              <CardTitle>Idea Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-start gap-2">
                  <Building className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500">Department</p>
                    <p className="font-medium text-slate-900">{idea.department}</p>
                  </div>
                </div>
                {idea.process_area && (
                  <div className="flex items-start gap-2">
                    <Clock className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500">Process Area</p>
                      <p className="font-medium text-slate-900">{idea.process_area}</p>
                    </div>
                  </div>
                )}
                {idea.location && (
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500">Location</p>
                      <p className="font-medium text-slate-900">{idea.location}</p>
                    </div>
                  </div>
                )}
                {idea.approver_name && (
                  <div className="flex items-start gap-2">
                    <User className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500">Approver</p>
                      <p className="font-medium text-slate-900">{idea.approver_name}</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Linked Process */}
          <Card>
            <CardContent className="pt-5">
              <ProcessSelector
                value={idea.linked_process_id}
                onChange={async (id, level) => {
                  try {
                    await api.put(`/ideas/${idea.id}`, { linked_process_id: id, linked_process_level: level });
                    setIdea(prev => ({ ...prev, linked_process_id: id, linked_process_level: level }));
                    toast.success(id ? 'Process linked' : 'Process unlinked');
                  } catch (err) { toast.error('Failed to update linked process'); }
                }}
                label="Linked Process"
                entityType="idea"
              />
            </CardContent>
          </Card>

          {/* Value Tracking */}
          {idea.value_metrics && (
            <Card>
              <CardHeader>
                <CardTitle>Value Tracking</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Baseline */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold text-slate-900 mb-2">Baseline</h4>
                    {idea.value_metrics.baseline_metric_name ? (
                      <>
                        <p className="text-sm text-slate-500">{idea.value_metrics.baseline_metric_name}</p>
                        <p className="text-2xl font-bold text-slate-900">
                          {idea.value_metrics.baseline_value} {idea.value_metrics.baseline_unit}
                        </p>
                        {idea.value_metrics.baseline_date && (
                          <p className="text-xs text-slate-400 mt-1">
                            Measured: {idea.value_metrics.baseline_date}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-slate-400">Not set</p>
                    )}
                  </div>

                  {/* Expected */}
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-semibold text-blue-900 mb-2">Expected</h4>
                    {idea.value_metrics.expected_value ? (
                      <>
                        <p className="text-2xl font-bold text-blue-900">
                          {idea.value_metrics.expected_value} {idea.value_metrics.expected_unit}
                        </p>
                        <StatusBadge status={idea.value_metrics.confidence || 'Estimated'} className="mt-2" />
                      </>
                    ) : (
                      <p className="text-slate-400">Not set</p>
                    )}
                  </div>

                  {/* Final (if available) */}
                  <div className="p-4 bg-emerald-50 rounded-lg">
                    <h4 className="font-semibold text-emerald-900 mb-2">Final</h4>
                    {idea.value_metrics.final_value ? (
                      <>
                        <p className="text-2xl font-bold text-emerald-900">
                          {idea.value_metrics.final_value} {idea.value_metrics.final_unit}
                        </p>
                        {idea.value_metrics.final_date && (
                          <p className="text-xs text-emerald-600 mt-1">
                            Measured: {idea.value_metrics.final_date}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-slate-400">Pending completion</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Comments */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Comments ({comments.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {comments.map((comment) => (
                  <div key={comment.id} className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-[#235894] flex items-center justify-center text-white text-sm font-medium">
                      {comment.user_name.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{comment.user_name}</span>
                        <span className="text-xs text-slate-400">
                          {new Date(comment.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-slate-700 mt-1">{comment.content}</p>
                    </div>
                  </div>
                ))}
                
                {comments.length === 0 && (
                  <p className="text-slate-400 text-center py-4">No comments yet</p>
                )}

                {/* Add comment */}
                <div className="flex gap-2 mt-4">
                  <Textarea
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    className="flex-1"
                    data-testid="comment-input"
                  />
                  <Button 
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || submitting}
                    data-testid="add-comment-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Approval Panel */}
          {canApprove() && (
            <Card className="border-2 border-[#235894]">
              <CardHeader>
                <CardTitle>Approval Required</CardTitle>
                <CardDescription>Review and take action on this idea</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {idea.status === 'Submitted' && (
                  <Button 
                    className="w-full bg-amber-500 hover:bg-amber-600"
                    onClick={handleStartReview}
                    disabled={submitting}
                    data-testid="start-review-btn"
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    Start Review
                  </Button>
                )}
                <Button 
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => { setApprovalAction('approve'); setShowApprovalDialog(true); }}
                  data-testid="approve-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
                <Button 
                  variant="outline"
                  className="w-full"
                  onClick={() => { setApprovalAction('request_changes'); setShowApprovalDialog(true); }}
                  data-testid="request-changes-btn"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Request Changes
                </Button>
                <Button 
                  variant="outline"
                  className="w-full text-red-600 hover:text-red-700"
                  onClick={() => { setApprovalAction('reject'); setShowApprovalDialog(true); }}
                  data-testid="reject-btn"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Inline Financial Review — shown to Approvers/Managers during review */}
          {canApprove() && reviewFinancials && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Financial & Value Assessment</CardTitle>
                <CardDescription>Complete during review — saved automatically on approval</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ValueTrackingForm
                  financialData={reviewFinancials}
                  operationalMetrics={reviewMetrics}
                  onChange={({ financial, operational }) => {
                    setReviewFinancials(financial);
                    setReviewMetrics(operational);
                  }}
                  showForecast={false}
                  showActuals={false}
                />
                <Button
                  onClick={handleSaveReviewData}
                  disabled={reviewSaving}
                  className="w-full bg-[#235894] hover:bg-[#1E4B80]"
                  data-testid="save-review-financials-btn"
                >
                  {reviewSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Save Review Data
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Priority Matrix */}
          {idea.effort_cost && (
            <Card>
              <CardHeader>
                <CardTitle>Priority Assessment</CardTitle>
              </CardHeader>
              <CardContent>
                <PriorityMatrix 
                  impactScore={idea.effort_cost.impact_score}
                  effortScore={idea.effort_cost.effort_score}
                />
                
                <Separator className="my-4" />
                
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Effort Estimate</span>
                    <StatusBadge status={idea.effort_cost.effort_estimate} />
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Complexity</span>
                    <StatusBadge status={idea.effort_cost.complexity} />
                  </div>
                </div>

                {idea.effort_cost.notes_for_approver && (
                  <>
                    <Separator className="my-4" />
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Notes for Approver</p>
                      <p className="text-sm text-slate-700">{idea.effort_cost.notes_for_approver}</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Budget Section — visible to all */}
          <Card className={idea.budget_approved ? 'border-emerald-300 bg-emerald-50/50' : ''}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Budget</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Display costs from effort_cost */}
              <div className="space-y-2 text-sm">
                {idea.effort_cost?.one_off_cost > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">One-off Cost</span>
                    <span className="font-medium">£{idea.effort_cost.one_off_cost.toLocaleString()}</span>
                  </div>
                )}
                {idea.effort_cost?.ongoing_cost > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Ongoing Cost</span>
                    <span className="font-medium">£{idea.effort_cost.ongoing_cost.toLocaleString()}/mo</span>
                  </div>
                )}
              </div>

              {/* Budget approval status */}
              {idea.budget_approved === true && (
                <div className="p-3 bg-emerald-100 rounded-lg border border-emerald-200">
                  <div className="flex items-center gap-2 text-emerald-800 mb-1">
                    <CheckCircle className="w-4 h-4" />
                    <span className="font-semibold text-sm">Budget Approved</span>
                  </div>
                  {idea.budget_amount && (
                    <p className="text-2xl font-bold text-emerald-700">£{idea.budget_amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  )}
                </div>
              )}
              {idea.budget_approved === false && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-2 text-amber-700 text-sm">
                    <Clock className="w-4 h-4" />
                    <span className="font-medium">Budget pending approval</span>
                  </div>
                </div>
              )}
              {idea.budget_approved == null && (
                <p className="text-xs text-slate-400">Budget status not yet set</p>
              )}

              {/* Editable budget controls — only for approvers during review */}
              {canApprove() && (
                <div className="pt-3 border-t space-y-3">
                  <div>
                    <Label className="text-sm">Budget Approved?</Label>
                    <div className="flex gap-2 mt-1">
                      <Button
                        size="sm"
                        variant={budgetApproved === true ? 'default' : 'outline'}
                        className={budgetApproved === true ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                        onClick={() => setBudgetApproved(true)}
                        data-testid="budget-yes-btn"
                      >Yes</Button>
                      <Button
                        size="sm"
                        variant={budgetApproved === false ? 'default' : 'outline'}
                        className={budgetApproved === false ? 'bg-amber-500 hover:bg-amber-600' : ''}
                        onClick={() => setBudgetApproved(false)}
                        data-testid="budget-no-btn"
                      >No</Button>
                    </div>
                  </div>

                  {budgetApproved === true && (
                    <div>
                      <Label className="text-sm">Approved Budget Amount (£)</Label>
                      <Input
                        type="number"
                        value={budgetAmount}
                        onChange={(e) => setBudgetAmount(e.target.value)}
                        placeholder="e.g. 120000"
                        className="mt-1"
                        data-testid="budget-amount-input"
                      />
                    </div>
                  )}

                  {budgetApproved === false && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full"
                      onClick={() => setShowBudgetSubmitted(true)}
                      data-testid="submit-budget-btn"
                    >Submit Budget for Approval</Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Attachments */}
          <FileAttachments 
            entityType="idea" 
            entityId={id} 
            canEdit={canEdit()} 
          />

          {/* Process Maps */}
          <LinkedProcessMaps
            entityType="idea"
            entityId={id}
            entityName={idea?.title}
            readOnly={!canEdit()}
          />
        </div>
      </div>

      {/* Approval Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {approvalAction === 'approve' && 'Approve Idea'}
              {approvalAction === 'reject' && 'Reject Idea'}
              {approvalAction === 'request_changes' && 'Request Changes'}
            </DialogTitle>
            <DialogDescription>
              {approvalAction === 'reject' 
                ? 'Please provide a reason for rejection (required)'
                : 'Add an optional comment for this action'
              }
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Enter your comment..."
            value={approvalComment}
            onChange={(e) => setApprovalComment(e.target.value)}
            data-testid="approval-comment-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApprovalDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleApprovalAction}
              disabled={submitting || (approvalAction === 'reject' && !approvalComment)}
              className={approvalAction === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
              data-testid="confirm-approval-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to Project Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Convert to Project</DialogTitle>
            <DialogDescription>
              Create a new project from this approved idea
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="project-name">Project Name</Label>
              <Input
                id="project-name"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                data-testid="project-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="project-owner">Project Owner *</Label>
              <Select value={projectOwner} onValueChange={setProjectOwner}>
                <SelectTrigger data-testid="project-owner-select">
                  <SelectValue placeholder="Select owner" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start-date">Start Date *</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="start-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-date">Target End Date *</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={targetEndDate}
                  onChange={(e) => setTargetEndDate(e.target.value)}
                  data-testid="end-date-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConvertToProject}
              disabled={submitting || !projectOwner || !startDate || !targetEndDate}
              className="bg-[#26B8A7] hover:bg-[#1fa393]"
              data-testid="confirm-convert-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Budget Submitted Popup */}
      <Dialog open={showBudgetSubmitted} onOpenChange={setShowBudgetSubmitted}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Budget Pack Submitted</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <p className="text-slate-600">Project Budget pack submitted for approval</p>
            <p className="text-sm text-slate-400 mt-2">You will be notified once the budget has been reviewed.</p>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowBudgetSubmitted(false)} className="w-full bg-[#235894] hover:bg-[#1E4B80]">OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IdeaDetail;
